import { type FormEvent, useMemo, useState } from 'react';
import {
  useActivateUser,
  useAddUserRole,
  useCreateUser,
  useDeactivateUser,
  useDeleteUser,
  useRemoveUserRole,
  useUsers,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/store/authStore';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, type Column } from '@/components/ui/Table';
import type { User } from '@/api/types';

const PAGE_SIZE = 20;

const emptyCreateForm = { firstName: '', lastName: '', email: '' };

/**
 * The backend has no generic-update endpoint (see UsersController) - GET
 * /users returns the complete unfiltered list with no server-side
 * search/pagination support, so this page fetches it once and
 * filters/searches/paginates client-side. This whole page is SuperAdmin-only
 * (see routes/AppRouter.tsx) - SuperAdmin is the only role left to grant/
 * revoke here; every other capability (organizing, judging, competing) is
 * scoped membership managed from the organizer/competition UI instead.
 * Permanent deletion (DELETE /users/{id}) is irreversible.
 */
export function AdminUsersPage() {
  const currentUser = useAuthStore((s) => s.user);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const users = useUsers();
  const addRole = useAddUserRole();
  const removeRole = useRemoveUserRole();
  const activateUser = useActivateUser();
  const deactivateUser = useDeactivateUser();
  const deleteUser = useDeleteUser();
  const createUser = useCreateUser();

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  function closeCreate() {
    setCreating(false);
    setCreateForm(emptyCreateForm);
    createUser.reset();
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    await createUser.mutateAsync({
      email: createForm.email.trim(),
      firstName: createForm.firstName.trim(),
      lastName: createForm.lastName.trim(),
    });
    closeCreate();
  }

  const filtered = useMemo(() => {
    const items = users.data?.items ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (u) =>
        u.email.toLowerCase().includes(term) ||
        (u.displayName ?? '').toLowerCase().includes(term) ||
        (u.firstName ?? '').toLowerCase().includes(term) ||
        (u.lastName ?? '').toLowerCase().includes(term),
    );
  }, [users.data, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const mutationError = addRole.error ?? removeRole.error ?? activateUser.error ?? deactivateUser.error ?? deleteUser.error;

  const columns: Column<User>[] = [
    { header: 'Ime', accessor: (u) => u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—' },
    { header: 'E-pošta', accessor: (u) => u.email },
    {
      header: 'Status',
      accessor: (u) => (
        <Badge tone={u.isActive === false ? 'Rejected' : 'Accepted'}>
          {u.isActive === false ? 'Neaktiven' : 'Aktiven'}
        </Badge>
      ),
    },
    {
      header: 'Skrbnik sistema',
      accessor: (u) => {
        const checked = u.roles.includes('SuperAdmin');
        const pending =
          (addRole.isPending && addRole.variables?.id === u.id) ||
          (removeRole.isPending && removeRole.variables?.id === u.id);
        const isSelf = u.id === currentUser?.id;
        return (
          <label
            title={isSelf ? 'Ne morete odvzeti lastne vloge skrbnika sistema.' : undefined}
            className={`select-none rounded-full border px-2 py-0.5 text-xs transition-colors ${
              checked ? 'border-accent-ring bg-accent-subtle text-accent-text' : 'border-line-strong text-fg-secondary'
            } ${pending || (isSelf && checked) ? 'opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              className="hidden"
              checked={checked}
              disabled={pending || (isSelf && checked)}
              onChange={() => {
                if (checked) removeRole.mutate({ id: u.id, role: 'SuperAdmin' });
                else addRole.mutate({ id: u.id, role: 'SuperAdmin' });
              }}
            />
            {checked ? 'Da' : 'Ne'}
          </label>
        );
      },
    },
    {
      header: 'Dejanja',
      accessor: (u) => (
        <div className="flex gap-2">
          {u.isActive === false ? (
            <Button
              variant="secondary"
              size="sm"
              isLoading={activateUser.isPending && activateUser.variables === u.id}
              onClick={() => activateUser.mutate(u.id)}
            >
              Aktiviraj
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              isLoading={deactivateUser.isPending && deactivateUser.variables === u.id}
              onClick={() => {
                if (confirm(`Deaktiviraj uporabnika "${u.email}"?`)) deactivateUser.mutate(u.id);
              }}
            >
              Deaktiviraj
            </Button>
          )}
          {u.id !== currentUser?.id && (
            <Button
              variant="danger"
              size="sm"
              // Backend requires the account to already be deactivated before
              // it can be permanently deleted (see UsersService.DeleteAsync) -
              // a deliberate two-step confirmation so a live account can't be
              // erased in one click.
              disabled={u.isActive !== false}
              title={u.isActive !== false ? 'Najprej deaktivirajte uporabnika.' : undefined}
              isLoading={deleteUser.isPending && deleteUser.variables === u.id}
              onClick={() => {
                if (
                  confirm(
                    `Trajno izbriši uporabnika "${u.email}"? To bo izbrisalo tudi vse njegove prispevke, ocene in poročila. Tega dejanja ni mogoče razveljaviti.`,
                  )
                ) {
                  deleteUser.mutate(u.id);
                }
              }}
            >
              Izbriši
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-fg">Uporabniki</h1>
          <p className="text-sm text-fg-muted">
            Računi se ustvarijo z registracijo, ko jih organizator dodeli tekmovanju, ali ročno tukaj. Tukaj lahko
            podelite vlogo skrbnika sistema in aktivirate/deaktivirate račune.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>Nov uporabnik</Button>
      </div>

      {mutationError && <Alert variant="error">{getApiErrorMessage(mutationError)}</Alert>}

      <div className="max-w-sm">
        <Input
          label="Iskanje"
          placeholder="Iskanje po imenu ali e-pošti"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {users.isLoading && <PageSpinner />}
      {users.isError && <ErrorState message={getApiErrorMessage(users.error)} onRetry={() => users.refetch()} />}
      {users.isSuccess && filtered.length === 0 && <EmptyState title="Ni najdenih uporabnikov" />}
      {users.isSuccess && filtered.length > 0 && <Table columns={columns} rows={paged} getRowKey={(u) => u.id} />}

      {users.isSuccess && filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prejšnja
          </Button>
          <span className="text-sm text-fg-muted">
            Stran {currentPage} od {pageCount}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            Naslednja
          </Button>
        </div>
      )}

      {creating && (
        <Modal title="Nov uporabnik" onClose={closeCreate}>
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            {createUser.isError && <Alert variant="error">{getApiErrorMessage(createUser.error)}</Alert>}
            <Input
              label="Ime"
              value={createForm.firstName}
              onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
              required
            />
            <Input
              label="Priimek"
              value={createForm.lastName}
              onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
              required
            />
            <Input
              label="E-pošta"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              required
            />
            <p className="text-xs text-fg-muted">
              Uporabnik prejme e-pošto s povezavo, kjer si sam izbere geslo.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={closeCreate}>
                Prekliči
              </Button>
              <Button type="submit" isLoading={createUser.isPending}>
                Ustvari
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
